/**
 * Build connectivity probe `app_config` from intake Gate-0 data + optional runtime credentials.
 */

function utf8ToBase64(str) {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
  );
}

function basicAuthHeader(email, apiToken) {
  return `Basic ${utf8ToBase64(`${email}:${apiToken}`)}`;
}

/**
 * @param {Record<string, unknown>} gate0Data
 * @param {string|number|null} projectId
 * @param {{ bearerToken?: string, basicEmail?: string, basicApiToken?: string, apiKeyHeader?: string, apiKeyValue?: string }} credentials
 * @returns {{ app_config: object } | { error: string }}
 */
export function buildAppConfigFromIntake(gate0Data, projectId, credentials = {}) {
  const g = gate0Data || {};
  const baseUrl = String(g.base_url || "").trim().replace(/\/+$/, "");

  if (!baseUrl) {
    return {
      error:
        "No base URL declared. Add an API base URL on Gate 1 readiness before running connectivity checks.",
    };
  }
  if (!/^https?:\/\//i.test(baseUrl)) {
    return {
      error: "Base URL must start with https:// or http://.",
    };
  }

  const rawEndpoints = Array.isArray(g.endpoints) ? g.endpoints : [];
  const endpoints = rawEndpoints
    .map((ep, i) => {
      const operation = String(ep.operation || ep.purpose || ep.intent || "").trim();
      return {
        id: ep.id || `ep-${String(i + 1).padStart(3, "0")}`,
        method: String(ep.method || "GET").toUpperCase(),
        path: String(ep.path || "").trim(),
        operation,
        purpose: operation || String(ep.purpose || "").trim() || `Endpoint ${i + 1}`,
        request_body: ep.request_body,
        payloads: Array.isArray(ep.payloads) ? ep.payloads : undefined,
      };
    })
    .filter((e) => e.path.length > 0);

  if (endpoints.length === 0) {
    return {
      error:
        "No API endpoints declared. Add at least one endpoint on Gate 1 readiness before running checks.",
    };
  }

  const bearer = String(credentials.bearerToken || "").trim();
  const basicEmail = String(credentials.basicEmail || "").trim();
  const basicTok = String(credentials.basicApiToken || "").trim();
  const kHeader = String(credentials.apiKeyHeader || "").trim();
  const kVal = String(credentials.apiKeyValue || "").trim();
  const authType = String(g.auth_type || "").toLowerCase();

  let authConfigs = {};
  let defaultAuthRef = null;

  if (basicEmail && basicTok) {
    authConfigs["auth-main"] = {
      type: "api_key",
      header: "Authorization",
      value: basicAuthHeader(basicEmail, basicTok),
    };
    defaultAuthRef = "auth-main";
  } else if (bearer) {
    authConfigs["auth-main"] = {
      type: "bearer_jwt",
      header: "Authorization",
      token: bearer,
    };
    defaultAuthRef = "auth-main";
  } else if (kHeader && kVal) {
    authConfigs["auth-main"] = {
      type: "api_key",
      header: kHeader,
      value: kVal,
    };
    defaultAuthRef = "auth-main";
  } else if (
    !authType ||
    authType.includes("none") ||
    authType.includes("public")
  ) {
    authConfigs["auth-none"] = { type: "none" };
    defaultAuthRef = "auth-none";
  } else {
    return {
      error:
        "Provide credentials below (Bearer token, Basic email + API token, or API key) to run connectivity checks.",
    };
  }

  let rateLimitRpm = 60;
  const rl = String(g.rate_limits || "");
  const m = rl.match(/(\d+)/);
  if (m) rateLimitRpm = parseInt(m[1], 10);

  const app_config = {
    app_id: projectId ? `project-${projectId}` : `intake-${Date.now()}`,
    app_name: String(g.app_name || "AI System"),
    base_url: baseUrl,
    rate_limit_rpm: rateLimitRpm,
    ssl_verify: true,
    auth_configs: authConfigs,
    endpoints: endpoints.map((e) => ({
      id: e.id,
      method: e.method,
      path: e.path.startsWith("/") ? e.path : `/${e.path}`,
      operation: e.operation || "",
      purpose: e.purpose,
      auth_ref: defaultAuthRef,
      request_schema: {},
      response_schema: {},
      test_data_source: { type: "synthetic" },
      risk_scope: [],
      ...(e.payloads?.length ? { payloads: e.payloads } : {}),
      ...(e.request_body !== undefined ? { request_body: e.request_body } : {}),
    })),
    collection: null,
  };

  return { app_config };
}

const STORAGE_PREFIX = "validaite_readiness_";

export function loadReadinessCache(projectId) {
  if (!projectId) return null;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${projectId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveReadinessCache(projectId, data) {
  if (!projectId) return;
  try {
    const existing = loadReadinessCache(projectId) || {};
    sessionStorage.setItem(
      `${STORAGE_PREFIX}${projectId}`,
      JSON.stringify({ ...existing, ...data })
    );
  } catch {
    /* ignore quota */
  }
}
